import { showHttpError, showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { setFormErrors } from 'actions/form'
import { createGroup, createUserGroupRelationships, listGroups } from 'features/user_groups'

import {
  ADD_TO_EXISTING_ACTION,
  CREATE_AND_ADD_ACTION,
  STATE_INITIAL,
  STATE_WITH_GROUPS,
  STATE_WITH_NO_GROUPS,
  USER_GROUPS_PAGE_SIZE
} from './constants'
import { userGroupsFilterSelector, userGroupsNextPageSelector } from './selectors'

export const fetchUserGroups = createAsyncThunk(
  'addUsersToGroup/fetchUserGroups',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState()
    const filter = userGroupsFilterSelector(state)
    const page = userGroupsNextPageSelector(state)
    const [success, response] = await listGroups({ filter_term: filter, page, page_size: USER_GROUPS_PAGE_SIZE })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    return response
  }
)

const createGroupAndAddSelected = createAsyncThunk(
  'addUsersToGroup/createGroupAndAddSelected',
  async ({ name, organizationMembershipIds, organizationInvitationIds }, { dispatch, rejectWithValue }) => {
    const [createSuccess, createResponse] = await createGroup({ name })

    if (!createSuccess) {
      dispatch(setFormErrors(createResponse))
      return rejectWithValue(createResponse)
    }

    const hasUsers = !!organizationMembershipIds?.length && organizationMembershipIds.length > 0
    const hasInvitations = !!organizationInvitationIds?.length && organizationInvitationIds.length > 0

    const [addSuccess, addResponse] = await createUserGroupRelationships(
      createResponse.id,
      {
        ...(hasUsers && { organization_membership_ids: organizationMembershipIds }),
        ...(hasInvitations && { organization_invitation_ids: organizationInvitationIds }),
        role: 'member'
      },
    )

    if (!addSuccess) {
      dispatch(showHttpError(addResponse))
      return rejectWithValue(addResponse)
    }

    dispatch(showSuccess(I18n.t('publisher.flash.saved')))

    return addResponse
  }
)

const addSelectedToGroup = createAsyncThunk(
  'addUsersToGroup/addSelectedToGroup',
  async ({ existingGroupId, organizationMembershipIds, organizationInvitationIds }, { dispatch, rejectWithValue }) => {
    const hasUsers = !!organizationMembershipIds?.length && organizationMembershipIds.length > 0
    const hasInvitations = !!organizationInvitationIds?.length && organizationInvitationIds.length > 0

    const [success, response] = await createUserGroupRelationships(
      existingGroupId,
      {
        ...(hasUsers && { organization_membership_ids: organizationMembershipIds }),
        ...(hasInvitations && { organization_invitation_ids: organizationInvitationIds }),
        role: 'member'
      },
    )

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    dispatch(showSuccess(I18n.t('publisher.flash.saved')))
    return response
  }
)

const initialState = {
  userGroups: null,
  userGroupsLoading: false,
  filter: '',
  nextPage: null,
  state: STATE_INITIAL,
  completed: false
}

const slice = createSlice({
  name: 'addUsersToGroup',
  initialState,
  reducers: {
    setFilter: (state, { payload }) => {
      state.filter = payload
    },
    setNextPage(state, { payload }) {
      state.nextPage = payload
    },
    setCompleted(state, { payload }) {
      state.completed = payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserGroups.pending, (state) => {
      state.userGroupsLoading = true
      if (state.nextPage === 1) {
        state.userGroups = []
      }
    })
    builder.addCase(fetchUserGroups.fulfilled, (state, { payload }) => {
      state.userGroupsLoading = false
      state.nextPage = payload.pagination.next_page
      if (state.state === STATE_INITIAL) {
        state.state = payload.collection.length > 0 ? STATE_WITH_GROUPS : STATE_WITH_NO_GROUPS
      }
      if (payload.pagination.current_page === 1) {
        state.userGroups = payload.collection
      } else {
        state.userGroups = state.userGroups.concat(payload.collection)
      }
    })
    builder.addCase(fetchUserGroups.rejected, (state) => {
      state.userGroupsLoading = false
      state.nextPage = null
      state.userGroups = []
    })
    builder.addCase(createGroupAndAddSelected.fulfilled, (state, _) => {
      state.completed = true
    })
    builder.addCase(addSelectedToGroup.fulfilled, (state, _) => {
      state.completed = true
    })
  }
})

export const { reducer, actions: { setCompleted } } = slice

export const searchUserGroups = (query) => (dispatch, getState) => {
  const previousQuery = userGroupsFilterSelector(getState())
  if (query === previousQuery) return

  dispatch(slice.actions.setFilter(query))
  dispatch(slice.actions.setNextPage(1))
  dispatch(fetchUserGroups())
}

export const addToGroup = ({ action, name, existingGroupId, organizationMembershipIds, organizationInvitationIds }) => async (dispatch) => {
  if (action === CREATE_AND_ADD_ACTION) {
    await dispatch(createGroupAndAddSelected({ name, organizationMembershipIds, organizationInvitationIds }))
  } else if (action === ADD_TO_EXISTING_ACTION) {
    await dispatch(addSelectedToGroup({ existingGroupId, organizationMembershipIds, organizationInvitationIds }))
  }
}
